import {
  ReactNode,
  Children,
  useRef,
  useEffect,
  useState,
  useCallback,
  useMemo,
  MouseEvent,
  cloneElement,
  ReactElement,
  isValidElement,
  JSXElementConstructor,
  ReactPortal,
} from 'react'

import classNames from 'classnames/bind'
import { groupBy } from 'ramda'

import IconDropDown from 'app/common/icons/dropdown.svg'
import { Scrollbars } from 'react-custom-scrollbars-2'
import useCallbackRef from 'app/common/hooks/useCallbackRef'
import { DotsLoader } from 'app/common/templates-next/page-template'

import calcListHeight from './calc-list-height'
import useCorrectPosition from './use-correct-position'
import HTMLSelect from './html-select'
import { dispatchFormChanged } from '../custom-events'

import styles from './plain-dropdown.module.scss'

const cx = classNames.bind(styles)
const emptyArray = []
const findFocus = () => document.activeElement

type ItemProps = {
  id: unknown
  disabled?: boolean
  active?: boolean
  selected?: boolean
}

const childrenKnownElements = (
  children: Array<
    ReactElement<ItemProps | JSXElementConstructor<ItemProps>> | ReactPortal | null
  > | null,
): Array<ReactElement<ItemProps>> => {
  return Children.toArray(children).filter((child): child is ReactElement<ItemProps> => {
    return isValidElement<ItemProps>(child) && !('children' in child && child.type === undefined)
  })
}

export type PlainDropdownProps<T extends ItemProps> = {
  id: string
  label?: string
  labelId?: string
  placeholder?: ReactNode
  children: Array<ReactElement<ItemProps> | null> | null
  className?: string
  arrowClassName?: string
  buttonClassName?: string
  itemClassName?: string
  itemsContainerClassName?: string
  labelClassName?: string
  defaultSkin?: boolean
  disabled?: boolean
  loading?: boolean
  errored?: boolean
  hideLabel?: boolean
  keepSelectedElementInList?: boolean
  onChange?: (targetProps: T) => void | Promise<void>
  onCollapseListbox?: () => void
}

const PlainDropdown = <T extends { id: unknown }>({
  id,
  label,
  labelId: labelIdProp,
  placeholder,
  children,
  className,
  arrowClassName,
  buttonClassName,
  itemClassName,
  itemsContainerClassName,
  labelClassName,
  defaultSkin,
  disabled,
  loading,
  errored,
  hideLabel,
  keepSelectedElementInList,
  onChange,
  onCollapseListbox,
}: PlainDropdownProps<T>) => {
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const itemsRef = useRef<HTMLUListElement | null>(null)
  const ddRef = useRef<HTMLDivElement | null>(null)

  const onCollapseListboxRef = useCallbackRef(onCollapseListbox)

  /**
   *  Перемещение фокуса внутри списка
   */

  const moveFocusInsideList = useCallback((direction: string) => {
    const currentFocus = findFocus()
    if (!currentFocus) return
    const listDOMElement = itemsRef.current
    if (!listDOMElement) return
    const items = Array.from(listDOMElement.querySelectorAll('li'))
    const number = items.length

    switch (direction) {
      case 'first': {
        items[0].focus()
        break
      }
      case 'next': {
        const whichOne = items.indexOf(currentFocus as HTMLLIElement)
        if (whichOne === number - 1 && buttonRef.current) {
          buttonRef.current.focus()
          break
        }
        items[whichOne + 1].focus()
        break
      }
      case 'prev': {
        const whichOne = items.indexOf(currentFocus as HTMLLIElement)
        if (whichOne === 0 && buttonRef.current) {
          buttonRef.current.focus()
          break
        }
        items[whichOne - 1].focus()
        break
      }
      case 'last': {
        items[number - 1].focus()
        break
      }
      default:
    }
  }, [])

  /**
   *  Открытие/закрытие списка
   */

  const [listboxHidden, setListboxHidden] = useState(true)

  useCorrectPosition(buttonRef, ddRef, listboxHidden)

  const hanldeToggleListbox = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      const { clientX, clientY } = e
      if (disabled) return
      if (buttonRef.current && clientX !== 0 && clientY !== 0) {
        buttonRef.current.blur()
      }
      setListboxHidden((state) => {
        if (buttonRef.current) {
          buttonRef.current.setAttribute('aria-expanded', String(state))
        }
        return !state
      })
    },
    [disabled],
  )

  const handleShowListbox = useCallback(() => {
    if (buttonRef.current) {
      buttonRef.current.setAttribute('aria-expanded', 'true')
      setListboxHidden(false)
    }
  }, [])

  const handleCollapseListbox = useCallback(() => {
    if (buttonRef.current) {
      buttonRef.current.setAttribute('aria-expanded', 'false')
      setListboxHidden(true)
    }
  }, [])

  /**
   *  Закрытие при клике вне dropdown
   */

  const handleClickOutside = useCallback(
    (e: globalThis.MouseEvent) => {
      if (!(e.target as HTMLElement).closest(`#${id}`)) {
        onCollapseListboxRef.current?.()
        handleCollapseListbox()
      }
    },
    [onCollapseListboxRef, handleCollapseListbox, id],
  )

  useEffect(() => {
    window.addEventListener('click', handleClickOutside)
    window.addEventListener('scroll', handleCollapseListbox)
    return () => {
      window.removeEventListener('click', handleClickOutside)
      window.removeEventListener('scroll', handleCollapseListbox)
    }
  }, [handleClickOutside]) // eslint-disable-line react-hooks/exhaustive-deps

  /**
   *  Группировка активный (выбранный) элемент и другие
   */

  const { items = emptyArray, selectedItem = emptyArray } = useMemo(() => {
    const childrenElements = childrenKnownElements(children)

    if (keepSelectedElementInList) {
      let SelectedComponent: ReactElement<ItemProps> | undefined
      const elementsWithActive = childrenElements.map((component) => {
        const {
          props: { selected },
        } = component
        if (selected) {
          SelectedComponent = component
          return cloneElement(component, {
            ...component.props,
            active: true,
          })
        }
        return component
      })
      return { items: elementsWithActive, selectedItem: [SelectedComponent] }
    }

    return groupBy((component) => {
      const {
        props: { selected },
      } = component
      return selected ? 'selectedItem' : 'items'
    }, childrenElements)
  }, [children, keepSelectedElementInList])

  /**
   *  Обработка события выбора элемента
   */

  const handleClickElement = useCallback(
    (e?: MouseEvent<HTMLElement>) => {
      const isMouseClick = Boolean(e && e.clientX && e.clientY)
      const currentFocus = findFocus()

      if (currentFocus && currentFocus.id && onChange) {
        const clickedItemId = currentFocus.id.replace(/^.+_item_/, '')
        const clickedComponent = items.find((component) => {
          const {
            props: { id: itemId },
          } = component
          return String(itemId) === clickedItemId
        })

        if (clickedComponent) {
          e?.stopPropagation()
          const { props: targetProps } = clickedComponent
          if (targetProps && !targetProps.disabled) {
            handleCollapseListbox()
            // выделяем элемент фокусом, только если был выбор клавиатурой (не мышью)
            if (buttonRef.current && !isMouseClick) {
              buttonRef.current.focus()
            }
            dispatchFormChanged()
            onChange(targetProps as T)
          }
        }
      }
    },
    [items, onChange, handleCollapseListbox],
  )

  /**
   *  Обработка событий с клавиатуры
   */

  const handleDoKeyAction = useCallback(
    (e) => {
      e.stopPropagation()
      const currentFocus = findFocus()
      const whichKey = e.key

      switch (whichKey) {
        case 'Enter':
          handleClickElement()
          break
        case 'ArrowDown':
          if (buttonRef.current && currentFocus === buttonRef.current) {
            handleShowListbox()
            moveFocusInsideList('first')
            break
          }
          moveFocusInsideList('next')
          break
        case 'ArrowUp':
          if (buttonRef.current && currentFocus === buttonRef.current) {
            handleShowListbox()
            moveFocusInsideList('last')
            break
          }
          moveFocusInsideList('prev')
          break
        case 'Escape':
          handleCollapseListbox()
          if (buttonRef.current) {
            buttonRef.current.focus()
          }
          break
        default:
      }
    },
    [handleClickElement, handleShowListbox, moveFocusInsideList, handleCollapseListbox],
  )

  /**
   *   Коррекция размеров  при открытии списка
   */

  const [autoHeightMax, setAutoHeightMax] = useState(0)
  const [listMinWidth, setListMinWidth] = useState(0)
  useEffect(() => {
    if (!listboxHidden) {
      const height = calcListHeight(itemsRef.current, buttonRef.current)
      setAutoHeightMax(height)
      setListMinWidth(buttonRef.current?.getBoundingClientRect().width || 0)
    }
  }, [listboxHidden])

  /**
   *  Связь с label
   */

  const labelId = label ? `label_${id}` : labelIdProp

  const buttonContentClasses = cx(
    styles.buttonContent,
    {
      defaultSkinBorder: defaultSkin && listboxHidden && !errored,
      defaultSkinBorderActive: defaultSkin && !listboxHidden,
      defaultSkinButtonContent: defaultSkin,
      defaultSkinBorderError: defaultSkin && errored,
      disabled,
    },
    buttonClassName,
  )

  const ddIconClasses = cx(
    styles.ddIconContainer,
    {
      disabled,
      ddIconContainerAdditionalBorder: !defaultSkin,
    },
    arrowClassName,
  )

  const arrowSkin = defaultSkin ? (
    <div className={cx(styles.defaultSkinDDIcon, { flip: !listboxHidden })} />
  ) : (
    <IconDropDown className={cx(styles.ddIcon, { flip: !listboxHidden })} />
  )

  const listContainerClasses = cx(
    styles.listContainer,
    {
      listboxHidden,
      defaultSkinListContainer: defaultSkin,
    },
    itemsContainerClassName,
  )

  const listItemClasses = cx(
    styles.listItem,
    {
      defaultSkinBorder: defaultSkin && listboxHidden,
      defaultSkinBorderActive: defaultSkin && !listboxHidden,
      defaultSkinListItem: defaultSkin,
    },
    itemClassName,
  )

  return (
    <>
      {label && (
        <label htmlFor={id} id={labelId} className={cx(labelClassName, { hidden: hideLabel })}>
          {label}
        </label>
      )}

      <div
        className={cx(styles.main, className)}
        onClick={handleClickElement}
        onKeyUp={handleDoKeyAction}
      >
        <button
          id={id}
          type="button"
          ref={buttonRef}
          aria-haspopup="listbox"
          onClick={hanldeToggleListbox}
          className={cx(styles.button, { disabled })}
          tabIndex={disabled ? -1 : 0}
        >
          <span className={buttonContentClasses}>
            {selectedItem[0] || placeholder}
            <span className={ddIconClasses}>{arrowSkin}</span>
          </span>
        </button>

        <div
          className={listContainerClasses}
          ref={ddRef}
          style={listMinWidth ? { minWidth: listMinWidth } : undefined}
        >
          <Scrollbars autoHeight autoHeightMin={0} autoHeightMax={autoHeightMax}>
            <ul ref={itemsRef} role="listbox" aria-labelledby={labelId} className={cx(styles.list)}>
              {items.map((Component) => {
                if (typeof Component === 'object' && 'props' in Component) {
                  const {
                    key,
                    props: { id: itemId },
                  } = Component
                  return (
                    <li
                      key={key}
                      className={listItemClasses}
                      id={`${id}_item_${itemId}`}
                      role="option"
                      tabIndex={-1}
                    >
                      {Component}
                    </li>
                  )
                }
                return null
              })}
            </ul>
          </Scrollbars>
        </div>

        {loading && <DotsLoader className={styles.loader} />}
      </div>

      {typeof selectedItem[0] === 'object' && 'props' in selectedItem[0] && (
        <HTMLSelect name={id} value={String(selectedItem[0].props.id)}>
          {selectedItem[0].props.id ? <option value={String(selectedItem[0].props.id)} /> : null}
        </HTMLSelect>
      )}
    </>
  )
}

export default PlainDropdown
